import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  LinearProgress,
  Chip,
  Grid,
  Paper
} from '@mui/material';
import { API_ENDPOINTS } from '../config';
import uploadService from '../services/uploadService';

const EditSubmission = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [currentPdfFile, setCurrentPdfFile] = useState('');
  const [currentZipFile, setCurrentZipFile] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // New state for chunked uploads
  const [pdfUploadProgress, setPdfUploadProgress] = useState(0);
  const [zipUploadProgress, setZipUploadProgress] = useState(0);
  const [pdfUploadStatus, setPdfUploadStatus] = useState('idle');
  const [zipUploadStatus, setZipUploadStatus] = useState('idle');

  const fetchSubmission = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.SUBMISSIONS}/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch submission');
      }

      const submission = await response.json();
      setName(submission.name);
      setCurrentPdfFile(submission.pdf_path);
      setCurrentZipFile(submission.zip_path);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching submission:', error);
      setError('Failed to fetch submission');
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);

  const handleFileUpload = async (file, fileType, setProgress, setStatus) => {
    if (!file) return null;

    try {
      setStatus('uploading');
      const uploadId = await uploadService.startUpload(
        'edit', // Use a placeholder task name for edits
        fileType,
        file,
        (progress) => {
          setProgress(progress);
        }
      );
      setStatus('completed');
      return uploadId;
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      setStatus('failed');
      setError(`Failed to upload ${fileType}: ${error.message}`);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsUploading(true);

    if (!name) {
      setError('Please fill in the submission name');
      setIsUploading(false);
      return;
    }

    try {
      let pdfId = null;
      let zipId = null;

      // Upload PDF file if provided
      if (pdfFile) {
        pdfId = await handleFileUpload(
          pdfFile, 
          'pdf', 
          setPdfUploadProgress, 
          setPdfUploadStatus
        );
        if (!pdfId) {
          setIsUploading(false);
          return;
        }
      }

      // Upload ZIP file if provided
      if (zipFile) {
        zipId = await handleFileUpload(
          zipFile, 
          'zip', 
          setZipUploadProgress, 
          setZipUploadStatus
        );
        if (!zipId) {
          setIsUploading(false);
          return;
        }
      }

      // Update submission
      const formData = new FormData();
      formData.append('name', name);
      if (pdfId) {
        formData.append('pdf_upload_id', pdfId);
      }
      if (zipId) {
        formData.append('zip_upload_id', zipId);
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to update a submission');
        navigate('/login');
        setIsUploading(false);
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.SUBMISSIONS}/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Your session has expired. Please log in again.');
          localStorage.removeItem('token');
          navigate('/login');
          setIsUploading(false);
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update submission');
      }

      setSuccess('Submission updated successfully');
      setTimeout(() => {
        navigate(`/submissions/${id}`);
      }, 2000);
    } catch (error) {
      console.error('Error updating submission:', error);
      setError(error.message || 'Failed to update submission. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (file, fileType) => {
    if (fileType === 'pdf') {
      setPdfFile(file);
      setPdfUploadStatus('idle');
      setPdfUploadProgress(0);
    } else {
      setZipFile(file);
      setZipUploadStatus('idle');
      setZipUploadProgress(0);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'uploading': return 'primary';
      case 'failed': return 'error';
      case 'paused': return 'warning';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'uploading': return 'Uploading...';
      case 'failed': return 'Failed';
      case 'paused': return 'Paused';
      default: return 'Ready';
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {error}
            </Typography>
            {(error.includes('session has expired') || error.includes('must be logged in')) && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => navigate('/login')}
                sx={{ mt: 1, mr: 1 }}
              >
                Go to Login
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => navigate(`/submissions/${id}`)}
              sx={{ mt: 1 }}
            >
              Back to Submission
            </Button>
          </Box>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Edit Submission
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Submission Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
            name="name"
            disabled={isUploading}
          />

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Extended Abstract (PDF)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Current: {currentPdfFile.split('/').pop()}
                </Typography>
                <input
                  accept=".pdf"
                  type="file"
                  onChange={(e) => handleFileChange(e.target.files[0], 'pdf')}
                  name="pdf_file"
                  disabled={isUploading}
                />
                {pdfFile && (
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={getStatusText(pdfUploadStatus)}
                      color={getStatusColor(pdfUploadStatus)}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {pdfUploadStatus === 'uploading' && (
                      <LinearProgress 
                        variant="determinate" 
                        value={pdfUploadProgress} 
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Submission Files (ZIP)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Current: {currentZipFile.split('/').pop()}
                </Typography>
                <input
                  accept=".zip"
                  type="file"
                  onChange={(e) => handleFileChange(e.target.files[0], 'zip')}
                  name="zip_file"
                  disabled={isUploading}
                />
                {zipFile && (
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={getStatusText(zipUploadStatus)}
                      color={getStatusColor(zipUploadStatus)}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {zipUploadStatus === 'uploading' && (
                      <LinearProgress 
                        variant="determinate" 
                        value={zipUploadProgress} 
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isUploading || !name}
            sx={{ mt: 3 }}
          >
            {isUploading ? 'Updating Submission...' : 'Update Submission'}
          </Button>
        </form>
      </Paper>
    </Container>
  );
};

export default EditSubmission; 